import React, { useEffect } from 'react'

import { GlobalStyles } from '@mui/material'
import { useLocation } from 'react-router-dom'
import Intercom, { boot, shutdown, update } from '@intercom/messenger-js-sdk'

import * as Api from 'redux/api/internal/Chat'

import { useCustomer } from 'redux/hooks/Customer'
import { useIsMobile } from 'utils/hooks'

interface LiveChatProps {
  user: {
    email: string
    id: string
    name: string
  }
}

// Intercom has no z-index option; clamp its containers below drawers/modals.
// The launcher wrapper is a fixed div with a hashed class (z-index 2147483000)
// and its own stacking context, so clamping the iframe is not enough: also
// clamp the direct children of .intercom-app.
const INTERCOM_CONTAINERS = [
  '.intercom-namespace',
  '.intercom-lightweight-app',
  '.intercom-launcher-frame',
  '.intercom-messenger-frame',
  '.intercom-notifications-frame',
  '.intercom-namespace .intercom-app > div'
].join(', ')

const INTERCOM_API_BASE = 'https://api-iam.eu.intercom.io'

// The backend signs the Intercom JWT for one hour. The age is checked against
// the wall clock every minute, so a token that expired while the computer
// slept is replaced shortly after wake-up.
const JWT_MAX_AGE_MS = 30 * 60 * 1000
const JWT_CHECK_INTERVAL_MS = 60 * 1000

const LiveChat = (props: LiveChatProps) => {
  const { id, name, email } = props.user
  const location = useLocation()

  const customer = useCustomer()
  const isMobile = useIsMobile()

  const isInternalPage = location.pathname.startsWith('/internal')
  const hideChat = isInternalPage || isMobile || Boolean(customer?.is_demo)

  // Intercom
  useEffect(() => {
    if (hideChat) return

    let cancelled = false
    let booted = false
    let fetchedAt = 0

    const fetchJwt = () => {
      fetchedAt = Date.now()

      Api.getIntercomJwt()
        .then((response) => {
          if (cancelled) return

          const { jwt } = response.data

          if (booted) {
            update({ intercom_user_jwt: jwt })

            return
          }

          const settings = {
            app_id: process.env.REACT_APP_INTERCOM_APP_ID ?? '',
            intercom_user_jwt: jwt, // 🔐
            user_id: id,
            name,
            email,
            session_duration: 86400000 // 1 day
          }

          // The first call loads the Messenger script, which boots from
          // these settings. Later boots (after shutdown) reuse the script.
          if (window.Intercom) {
            boot({ ...settings, api_base: INTERCOM_API_BASE })
          } else {
            Intercom({ ...settings, region: 'eu' })
          }

          booted = true
        })
        .catch(() => {
          // ignore
        })
    }

    fetchJwt()

    const interval = setInterval(() => {
      if (Date.now() - fetchedAt > JWT_MAX_AGE_MS) fetchJwt()
    }, JWT_CHECK_INTERVAL_MS)

    return () => {
      cancelled = true
      clearInterval(interval)

      if (booted) shutdown()
    }
  }, [hideChat, id, name, email])

  if (hideChat) return null

  return (
    <GlobalStyles
      styles={(theme) => ({
        [INTERCOM_CONTAINERS]: {
          zIndex: `${theme.zIndex.fab} !important`
        }
      })}
    />
  )
}

LiveChat.displayName = 'LiveChat'
export default LiveChat
